// HTTP client for the Oreia Worker API.
//
// Mirrors ios-app/.../Core/Network/APIClient.swift: every request carries the session JWT as a
// Bearer header, and the token is the only credential — there are no cookies, so nothing here
// depends on same-site behaviour.
//
// Streaming note: /api/oracle and /api/memories/{id}/discuss are POST routes that require the
// Authorization header, which EventSource cannot send. They must be consumed with fetch + a
// ReadableStream reader instead. See streamSSE below.

const DEFAULT_BASE_URL = 'https://api.oraclethejournal.com';

export const API_BASE_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') || DEFAULT_BASE_URL;

// Same storage key the previous web client used, so an existing signed-in session carries over
// rather than silently logging everyone out on cutover.
const TOKEN_KEY = 'oreiajournal_token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** 402/403 from an AI or premium route means the energy gate or paywall stopped us. */
  get isPaymentRequired(): boolean {
    return this.status === 402 || (this.status === 403 && (this.body as {code?:string}|undefined)?.code !== 'VERIFY_EMAIL_FOR_AI');
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

/** Raised when the session is gone, so callers can bounce to the login screen. */
export class SessionExpiredError extends ApiError {
  constructor() {
    super(401, 'Your session has expired. Please sign in again.');
    this.name = 'SessionExpiredError';
  }
}

type Json = Record<string, unknown> | unknown[];

interface RequestOptions {
  method?: string;
  body?: Json;
  /** Raw bytes to send instead of JSON — an encrypted photo. Mutually exclusive with `body`. */
  bytes?: Uint8Array;
  signal?: AbortSignal;
  /** Skip the Authorization header (login, signup, waitlist). */
  anonymous?: boolean;
}

async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const { method = 'GET', body, bytes, signal, anonymous } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (bytes !== undefined) headers['Content-Type'] = 'application/octet-stream';

  if (!anonymous) {
    const token = getToken();
    if (!token) throw new SessionExpiredError();
    headers.Authorization = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: bytes !== undefined ? (bytes as BodyInit) : body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (signal?.aborted) throw err;
    throw new ApiError(0, 'Could not reach Oreia. Check your connection and try again.');
  }

  if (res.status === 401) {
    clearToken();
    throw new SessionExpiredError();
  }

  if (!res.ok) {
    const parsed = await res.json().catch(() => null);
    const message =
      (parsed && typeof parsed === 'object' && 'message' in parsed && typeof parsed.message === 'string' ? parsed.message : null) ??
      (parsed && typeof parsed === 'object' && 'error' in parsed && typeof parsed.error === 'string'
        ? parsed.error
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message, parsed);
  }

  return res;
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await request(path, { signal });
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body?: Json, signal?: AbortSignal): Promise<T> {
  const res = await request(path, { method: 'POST', body: body ?? {}, signal });
  return res.status === 204 ? (undefined as T) : (res.json() as Promise<T>);
}

export async function apiPut<T>(path: string, body?: Json, signal?: AbortSignal): Promise<T> {
  const res = await request(path, { method: 'PUT', body: body ?? {}, signal });
  return res.json() as Promise<T>;
}

export async function apiPatch<T>(path: string, body?: Json): Promise<T> {
  const res = await request(path, { method: 'PATCH', body: body ?? {} });
  return res.json() as Promise<T>;
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await request(path, { method: 'DELETE' });
  return res.status === 204 ? (undefined as T) : (res.json() as Promise<T>);
}

/**
 * PUT raw bytes. Used for entry photos, which are already an AES-GCM box by the time they get here —
 * base64-ing them into a JSON field would add a third to every upload for nothing.
 */
export async function apiPutBytes<T>(
  path: string,
  bytes: Uint8Array,
  signal?: AbortSignal,
): Promise<T> {
  const res = await request(path, { method: 'PUT', bytes, signal });
  return res.json() as Promise<T>;
}

/** GET raw bytes, for the sealed photo the caller is about to decrypt. */
export async function apiGetBytes(path: string, signal?: AbortSignal): Promise<Uint8Array> {
  const res = await request(path, { signal });
  return new Uint8Array(await res.arrayBuffer());
}

/** Unauthenticated POST, for login/signup/waitlist. */
export async function apiPostAnonymous<T>(path: string, body: Json): Promise<T> {
  const res = await request(path, { method: 'POST', body, anonymous: true });
  return res.json() as Promise<T>;
}

/**
 * Consume one of the Worker's SSE routes.
 *
 * The Worker normalises every AI provider into the same frame shape (see
 * collective-vault-worker/src/sse.ts): `data: {"response":"<token>"}` per chunk, then
 * `data: [DONE]`. Chunks can split mid-frame across network reads, so we buffer until a
 * double newline before parsing.
 */
export async function streamSSE(
  path: string,
  body: Json,
  onToken: (token: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  const token = getToken();
  if (!token) throw new SessionExpiredError();

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
    signal,
  });

  if (res.status === 401) {
    clearToken();
    throw new SessionExpiredError();
  }
  if (!res.ok) {
    const parsed = await res.json().catch(() => null);
    const message =
      parsed && typeof parsed === 'object' && 'error' in parsed && typeof parsed.error === 'string'
        ? parsed.error
        : `Stream failed (${res.status})`;
    throw new ApiError(res.status, message, parsed);
  }
  if (!res.body) throw new ApiError(0, 'Oreia returned an empty response.');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Frames are separated by a blank line. Keep the trailing partial frame in the buffer.
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';

    for (const frame of frames) {
      const line = frame.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;

      const payload = line.slice(5).trim();
      if (payload === '[DONE]') return;
      if (!payload) continue;

      try {
        const parsed = JSON.parse(payload) as { response?: string; error?: string };
        if (parsed.error) throw new ApiError(500, parsed.error);
        if (parsed.response) onToken(parsed.response);
      } catch (err) {
        if (err instanceof ApiError) throw err;
        // A frame we can't parse is not worth failing the whole stream over.
      }
    }
  }
}
